const DEFAULT_CASH_RECON_SYSTEM_TYPE = 'CASH_RECON';

/**
 * Resolve Cash Recon ledger scope from header API.
 * Returns { systemType, glHeaderId } for the tenant.
 */
export async function fetchCashReconLedgerScope({
  baseUrl,
  tenantId,
  token,
  systemType = DEFAULT_CASH_RECON_SYSTEM_TYPE,
}) {
  if (!baseUrl) throw new Error('Backend base URL is required.');
  if (!tenantId) throw new Error('Tenant ID is required.');
  if (!token) throw new Error('Authentication token is required.');

  const url =
    `${baseUrl}conductor/gl-headers/by-tenant` +
    `?tenant_id=${encodeURIComponent(tenantId)}` +
    `&system_type=${encodeURIComponent(systemType)}`;

  const resp = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const data = await resp.json().catch(() => []);
  if (!resp.ok) {
    throw new Error(
      (data && (data.message || data.detail)) ||
        `Failed to fetch GL headers: ${resp.status} ${resp.statusText}`,
    );
  }
  if (!Array.isArray(data) || data.length === 0) {
    throw new Error('No GL headers found for tenant/system type.');
  }

  const normalizedRequestedSystemType = String(systemType).trim().toUpperCase();
  const matchingHeaders = data.filter(
    (h) =>
      h?.glHeaderId &&
      String(h?.systemType || '')
        .trim()
        .toUpperCase() === normalizedRequestedSystemType,
  );
  const header =
    matchingHeaders.find((h) => h?.glHeaderId) ||
    data.find((h) => h?.glHeaderId) ||
    data[0];
  if (!header?.glHeaderId) {
    throw new Error('GL header response missing glHeaderId.');
  }
  if (
    String(header?.systemType || '')
      .trim()
      .toUpperCase() !== normalizedRequestedSystemType
  ) {
    throw new Error(`No GL header found for tenant with system type ${systemType}.`);
  }

  /**
   * Use the requested `systemType` for downstream URLs/bodies — not necessarily
   * `header.systemType` from the payload (those can disagree with what we queried for).
   */
  return {
    systemType,
    glHeaderId: header.glHeaderId,
  };
}

/**
 * GL search responses are `{ content, total, limit, offset }`.
 * A bare array is accepted so a caller can still read an older payload.
 */
export function unwrapGlTransactionPage(data) {
  if (Array.isArray(data)) {
    return { content: data, total: data.length, limit: data.length, offset: 0 };
  }
  if (data && Array.isArray(data.content)) {
    const total = Number(data.total);
    return {
      content: data.content,
      total: Number.isFinite(total) ? total : data.content.length,
      limit: Number(data.limit) || data.content.length,
      offset: Number(data.offset) || 0,
    };
  }
  return null;
}

/** Append dynamic &system_type=&gl_header_id= to a URL that already has a ?... query string. */
export async function appendCashReconLedgerCoaQueryParams(url, { baseUrl, tenantId, token }) {
  const scope = await fetchCashReconLedgerScope({ baseUrl, tenantId, token });
  return `${url}&system_type=${encodeURIComponent(scope.systemType)}&gl_header_id=${encodeURIComponent(scope.glHeaderId)}`;
}
